package com.evoapps.evoshift.domain.repository

import com.evoapps.evoshift.domain.model.TimeRange
import com.evoapps.evoshift.domain.model.ClockAction
import com.evoapps.evoshift.domain.model.ClockOutcome
import com.evoapps.evoshift.domain.model.ClockReviewAction
import com.evoapps.evoshift.domain.model.LocationReading
import com.evoapps.evoshift.domain.model.ClaimOutcome
import com.evoapps.evoshift.domain.model.ConflictMetadata
import com.evoapps.evoshift.domain.model.BusyElsewhere
import com.evoapps.evoshift.domain.model.RemovedShift
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.ShiftManagerNotes
import com.evoapps.evoshift.domain.model.ShiftOverlap
import com.evoapps.evoshift.domain.model.SwapRequest
import com.evoapps.evoshift.domain.model.workerId
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map

interface ShiftRepository {
    // Reading Shifts
    fun observeShiftsForRoster(rosterId: String): Flow<List<Shift>>
    /**
     * My schedule: the shifts [userId] works from [fromDate] (SH-136: [ownSchedule], so also the
     * ones recorded with them as `actualUserId`, and not those a colleague was recorded on).
     */
    fun observeShiftsForUser(userId: String, fromDate: String): Flow<List<Shift>>

    /**
     * SH-259: the managers' notes on the month's shifts ([ShiftManagerNotes], their own documents,
     * managers and owners only), by shift id. The shift reads and writes of a store that keeps
     * them on the shift itself (the fakes) need none: the default is empty.
     */
    fun observeManagerNotes(rosterId: String): Flow<Map<String, ShiftManagerNotes>> = flowOf(emptyMap())

    /** SH-259: [observeManagerNotes] once. */
    suspend fun getManagerNotes(rosterId: String): Result<Map<String, ShiftManagerNotes>> = Result.success(emptyMap())

    /** SH-259: the notes on one shift; null: none. */
    suspend fun getManagerNote(shiftId: String): Result<ShiftManagerNotes?> = Result.success(null)

    // Single Shift Operations
    suspend fun getShift(shiftId: String): Result<Shift>
    suspend fun createShift(shift: Shift): Result<String>
    suspend fun createShifts(shifts: List<Shift>): Result<Unit>
    suspend fun updateShift(shift: Shift): Result<Unit>
    /** Writes many shifts in batches: publishing a month must not take one round trip per shift. */
    suspend fun updateShifts(shifts: List<Shift>): Result<Unit>
    suspend fun deleteShift(shiftId: String): Result<Unit>

    /**
     * SH-114: creates [shifts] in batches like [createShifts] and returns their new ids, in the
     * same order (so a copy can be undone). The default writes them one by one.
     */
    suspend fun createShiftsWithIds(shifts: List<Shift>): Result<List<String>> =
        runCatching { shifts.map { createShift(it).getOrThrow() } }

    /** SH-114: deletes the shifts in batches (undoing a copy). The default deletes them one by one. */
    suspend fun deleteShifts(shiftIds: List<String>): Result<Unit> =
        runCatching { shiftIds.forEach { deleteShift(it).getOrThrow() } }

    /**
     * SH-199: removes [replaced] and creates [shifts] (a copy that replaces days), together in one
     * batch where it holds both; returns the new ids in order. All or nothing: a failure leaves
     * the month as it was. The default deletes, then creates, and puts [replaced] back on failure.
     */
    suspend fun replaceShifts(replaced: List<Shift>, shifts: List<Shift>): Result<List<String>> = runCatching {
        deleteShifts(replaced.map { it.id }).getOrThrow()
        createShiftsWithIds(shifts).getOrElse { e ->
            restoreShifts(replaced)
            throw e
        }
    }

    // --- SH-263: published shifts removed since the last publish ---------------------------------
    // Every delete above (deleteShift, deleteShifts, replaceShifts, undoSplit) records a deleted
    // shift the team had ([com.evoapps.evoshift.domain.model.RosterPublishing.leavesRemoval]) as a
    // [RemovedShift] of its month, in the same write; publishing clears them.

    /** The month's published shifts deleted since its last publish (owners and managers). */
    fun observeRemovedShifts(rosterId: String): Flow<List<RemovedShift>> = flowOf(emptyList())

    /** Publishing: the month's removals are now what the team sees. */
    suspend fun clearRemovedShifts(rosterId: String): Result<Unit> = Result.success(Unit)

    /**
     * SH-271: a published shift was given to someone else ([assignUser]): it waits for the next
     * publish, which tells [publishedUserId] (who the team saw on it,
     * [com.evoapps.evoshift.domain.model.RosterPublishing.publishedAssignee]) and its new person.
     */
    suspend fun markReassigned(shiftId: String, publishedUserId: String?): Result<Unit> = Result.success(Unit)

    /**
     * Puts deleted shifts back under their own ids, exactly as they were (an Undo), dropping their
     * removal records: the team's shift is there again.
     */
    suspend fun restoreShifts(shifts: List<Shift>): Result<Unit> = updateShifts(shifts)


    /**
     * The person's shifts in one workplace between two ISO dates (inclusive). Queried per roster
     * month, so a manager reads only their own workplace (firestore.rules).
     */
    suspend fun getUserShiftsInWorkplace(workplaceId: String, userId: String, startDate: String, endDate: String): Result<List<Shift>>

    /**
     * SH-237: the shifts [userId] actually worked (or works) in one workplace between two ISO dates
     * (inclusive), [workerId][com.evoapps.evoshift.domain.model.workerId] == [userId]: their own
     * less those somebody else covered or nobody came to, plus the ones they covered for someone
     * (`actualUserId`) and their split parts. Read like [getUserShiftsInWorkplace] (per roster
     * month). The default, for repositories without it, keeps only the assigned ones they worked.
     */
    suspend fun getShiftsWorkedInWorkplace(workplaceId: String, userId: String, startDate: String, endDate: String): Result<List<Shift>> =
        getUserShiftsInWorkplace(workplaceId, userId, startDate, endDate).map { shifts -> shifts.filter { it.workerId == userId } }

    /**
     * The person's other shifts overlapping [shift]'s planned time, in every workplace (overnight
     * shifts from the day before included). Server-side: a manager may not read other workplaces.
     */
    suspend fun findOverlappingShifts(userId: String, shift: Shift): Result<List<ShiftOverlap>>

    /**
     * SH-135: the shifts of [userIds] at their other workplaces between [startDate] and [endDate]
     * (ISO dates, inclusive), for planning [workplaceId] (its managers only). From the server
     * (findBusyElsewhere): a client never reads another workplace's shifts; another employer's
     * show only once published, without the employer's name.
     */
    suspend fun findBusyElsewhere(workplaceId: String, userIds: List<String>, startDate: String, endDate: String): Result<List<BusyElsewhere>>

    /**
     * SH-165: for the person's own shift [shiftId], which active colleagues of its workplace said
     * they are available or unavailable that day (from the server: employees never read colleagues'
     * availability). A colleague missing from the map has not answered.
     */
    suspend fun swapAvailability(shiftId: String): Result<Map<String, com.evoapps.evoshift.domain.model.AvailabilityStatus>> =
        Result.failure(UnsupportedOperationException("swapAvailability"))

    // Marketplace / Status Updates
    suspend fun updateSwapRequest(shiftId: String, request: SwapRequest?): Result<Unit>

    /**
     * SH-164: puts back who works the shift, whether it is offered to the team, its conflict note
     * and its swap request, as they were before a swap approval (the approval's Undo). By default
     * two writes; the Firestore implementation writes them together.
     */
    suspend fun restoreSwap(shiftId: String, userId: String?, isOpen: Boolean, conflictMeta: ConflictMetadata?, request: SwapRequest?): Result<Unit> =
        assignUser(shiftId, userId, conflictMeta).mapCatching { updateSwapRequest(shiftId, request).getOrThrow() }

    /**
     * Writes only the actual times (and who changed them): what the assigned employee may change
     * on their own shift (clock in/out, correcting hours).
     */
    /**
     * SH-106: clocks in or out through the clockShift function, which checks [reading] against
     * the workplace's geofence and stores only the result and the distance. Fails with
     * ClockException for its refusals.
     */
    suspend fun clockWithLocation(shiftId: String, action: ClockAction, reading: LocationReading): Result<ClockOutcome> =
        Result.failure(UnsupportedOperationException("clockWithLocation"))

    suspend fun updateActualTime(shiftId: String, actual: TimeRange?, editorUserId: String): Result<Unit>

    /**
     * SH-165: the Undo of a clock-out recorded by the clockShift function (a geofence, a shift
     * covered for a colleague): the function clears the actual end again (UNDO_OUT), only for the
     * person's own clock-out of the last few minutes.
     */
    suspend fun undoClockOut(shiftId: String): Result<Unit> =
        Result.failure(UnsupportedOperationException("undoClockOut"))

    /**
     * SH-128: records who worked an unassigned shift afterwards: [workerId] as `actualUserId`, or
     * null for "nobody worked it" (`notWorked`). The planned assignee stays as it was. A manager's
     * write (firestore.rules).
     */
    suspend fun recordWorker(shiftId: String, workerId: String?, editorUserId: String): Result<Unit> =
        Result.failure(UnsupportedOperationException("recordWorker"))

    /**
     * SH-236: records who actually worked the shift: [actualUserId] (null: the assignee, as planned),
     * or that nobody came ([notWorked], with an optional [noShowReason]). The planned assignee
     * stays as it was. A manager's write (firestore.rules). The default rewrites the shift.
     */
    suspend fun recordActualWork(
        shiftId: String,
        actualUserId: String?,
        notWorked: Boolean,
        noShowReason: String?,
        editorUserId: String
    ): Result<Unit> = getShift(shiftId).mapCatching { shift ->
        updateShift(
            shift.copy(
                actualUserId = actualUserId, notWorked = notWorked, noShowReason = noShowReason,
                metadata = shift.metadata.copy(updatedBy = editorUserId)
            )
        ).getOrThrow()
    }

    /**
     * SH-236: writes a split: [first] (the original document, shortened) and [second] (a new
     * shift); returns the new id. All or nothing where the store allows it; the default writes the
     * first part, then creates the second and puts the first back if that fails.
     */
    suspend fun splitShift(original: Shift, first: Shift, second: Shift): Result<String> = runCatching {
        updateShift(first).getOrThrow()
        createShift(second).getOrElse { e ->
            updateShift(original)
            throw e
        }
    }

    /** SH-236: "Undo split": [restored] (the original again) written and [secondId] removed, together where possible. */
    suspend fun undoSplit(restored: Shift, secondId: String): Result<Unit> = runCatching {
        updateShift(restored).getOrThrow()
        deleteShift(secondId).getOrThrow()
    }

    /**
     * SH-134: clocks in at [workplaceId] without a shift of one's own (clockShift with no shift):
     * the server records it on a colleague's shift at that time or on a new shift, both pending a
     * manager, with the geofence check where the workplace has one.
     */
    suspend fun clockInUnplanned(workplaceId: String, reading: LocationReading): Result<ClockOutcome> =
        Result.failure(UnsupportedOperationException("clockInUnplanned"))

    /** SH-134: the shifts [userId] covers for colleagues (unplanned clock-ins), live. */
    fun observeCoveredShifts(userId: String): Flow<List<Shift>> = kotlinx.coroutines.flow.flowOf(emptyList())

    /**
     * SH-134: a manager's decision on flagged or unplanned clock times (the reviewClock function,
     * which also logs it). [start]/[end] only for [ClockReviewAction.EDIT_TIMES]. Returns whether
     * the shift was removed (a rejected extra shift).
     */
    suspend fun reviewClock(shiftId: String, action: ClockReviewAction, start: String? = null, end: String? = null): Result<Boolean> =
        Result.failure(UnsupportedOperationException("reviewClock"))

    /**
     * SH-257: the person enters the actual times of their own shift (the reportWorkHours function:
     * only where the workplace lets the team, within its days, before a manager confirmed them).
     * Refusals come as [com.evoapps.evoshift.domain.model.WorkHoursReportException].
     */
    suspend fun reportWorkHours(shiftId: String, start: String, end: String): Result<Unit> =
        Result.failure(UnsupportedOperationException("reportWorkHours"))

    /**
     * Puts [userId] on the shift. SH-267: [borrowedFrom] is the other workplace of the group they
     * are borrowed from (null: one of the workplace's own people, or nobody); always written, so a
     * shift handed back to the workplace's own staff is no longer borrowed.
     */
    suspend fun assignUser(
        shiftId: String,
        userId: String?,
        conflictMeta: ConflictMetadata?,
        borrowedFrom: String? = null
    ): Result<Unit>

    // Realtime Pending Requests (For Manager Dashboard)
    /**
     * Claims a marketplace shift for [claimantId] (the signed-in user) in one server-side step
     * (callable function claimShift): the workplace policy decides between assigning it now and
     * waiting for a manager. Fails with [com.evoapps.evoshift.domain.model.ClaimRefusedException].
     */
    suspend fun claimShift(shiftId: String, claimantId: String): Result<ClaimOutcome>

    fun observePendingSwaps(rosterId: String): Flow<List<Shift>>
    suspend fun getShiftsForRoster(rosterId: String): Result<List<Shift>>

    /**
     * SH-255: the roster month as the signed-in person may see it, from the server (teamRoster):
     * where the workplace limits what employees see of colleagues' shifts, or hides someone, the
     * rules let them read only their own and unassigned shifts directly. Colleagues' copies carry
     * no clock checks, notes, absences or reasons. Online only.
     */
    suspend fun getTeamRoster(rosterId: String): Result<List<Shift>> =
        Result.failure(UnsupportedOperationException("getTeamRoster"))

    /** SH-255: one shift through the server, when the caller may see it ([getTeamRoster]). */
    suspend fun getTeamShift(shiftId: String): Result<Shift> =
        Result.failure(UnsupportedOperationException("getTeamShift"))

    /**
     * SH-255: [userId]'s own shifts of one roster month: assigned to them or recorded with them as
     * who worked them (queries the rules allow whatever the workplace's setting).
     */
    suspend fun getOwnShiftsInRoster(rosterId: String, userId: String): Result<List<Shift>> = runCatching {
        observeOwnShiftsInRoster(rosterId, userId).first()
    }

    /** SH-255: [getOwnShiftsInRoster] as it changes. */
    fun observeOwnShiftsInRoster(rosterId: String, userId: String): Flow<List<Shift>> {
        val (year, month) = com.evoapps.evoshift.domain.model.RosterIds.yearMonthOf(rosterId)
        val monthStart = "$year-${month.toString().padStart(2, '0')}-01"
        return observeShiftsForUser(userId, monthStart).map { shifts -> shifts.filter { it.rosterId == rosterId } }
    }
}